export type CountryCode = keyof typeof EarTag.countryCodes;

export default class EarTag {
  static regexISO24631 =
    /^([0-9]{1})([0-9]{1})([0-9]{2})([0-9]{2})([0-9]{1})(372|826|899|528|250|276)([0-9]{7})([0-9]{5})/;

  static countryCodes: { [key: string]: string } = {
    IE: "372",
    UK: "826",
    XI: "899",
    NL: "528",
    FR: "250",
    DE: "276",
    BE: "056",
  };

  static get countryCodesByNumber(): { [code: string]: CountryCode } {
    let reversed: { [code: string]: CountryCode } = {};
    for (const key of Object.keys(EarTag.countryCodes) as Array<
      keyof typeof EarTag.countryCodes
    >) {
      reversed[EarTag.countryCodes[key]] = key;
    }
    return reversed;
  }

  private normalisedCountryCode(
    countryCode: string | null | undefined | null | number
  ): CountryCode | null {
    if (!countryCode || typeof countryCode !== "string") {
      return null;
    }
    switch (countryCode) {
      case "GB-ENG":
      case "GB-SCT":
      case "GB-WLS":
        return "UK";
      case "GB-NIR":
        return "XI";
      default:
        return countryCode as CountryCode;
    }
  }

  private _raw: string;
  get raw(): string {
    return this._raw;
  }

  // readonly properties
  private _isEartag: boolean = false;
  isEartag(): boolean {
    return this._isEartag;
  }

  private _isoCountryCode?: string;
  get isoCountryCode(): string | undefined {
    return this._isoCountryCode;
  }

  private _nationalIdentifier?: string;
  get nationalIdentifier(): string | undefined {
    return this._nationalIdentifier;
  }

  // private constructor
  constructor(
    input: string,
    fallbackCountryCode?: CountryCode | string | null
  ) {
    fallbackCountryCode = this.normalisedCountryCode(fallbackCountryCode);

    let workingInput = input.replace(/\s/g, "").replace(/[^ -~]+/g, "");
    this._raw = workingInput; // Store the initial cleaned input. May get updated if prefix is added.

    // Part 1: Handle explicit country codes (alpha or numeric) in the input string
    let parsedCountryCode: string | undefined;
    let parsedNationalIdentifier: string | undefined;

    // 1a. Alpha prefix
    if (workingInput.length > 2 && /^[A-Z]{2}/.test(workingInput)) {
      const alphaCode = workingInput.slice(0, 2);
      const numericCode = EarTag.countryCodes[alphaCode];
      if (numericCode) {
        // Replace alpha with numeric for further parsing
        workingInput = numericCode + workingInput.slice(2);
        this._raw = workingInput; // Update _raw as it now contains numeric country code
      }
      // If unknown alpha, workingInput remains as is, will be handled by numeric check or fallback
    }

    // 1b. Numeric country code (already in workingInput, or converted from alpha)
    const countryPattern = "(372|826|899|528|250|276|056)";

    const regex12 = new RegExp(
      `^[0-9]{0,7}(${countryPattern})([0-9]{7})([0-9]{5})`
    );
    let match12 = regex12.exec(workingInput);

    if (match12) {
      parsedCountryCode = match12[1];
      parsedNationalIdentifier = `${match12[3]}${match12[4]}`;
    } else {
      const regex11 = new RegExp(
        `^[0-9]{0,7}(${countryPattern})([0-9]{6})([0-9]{5})`
      );
      let match11 = regex11.exec(workingInput);
      if (match11) {
        parsedCountryCode = match11[1];
        parsedNationalIdentifier = `${match11[3]}${match11[4]}`;
      }
    }

    // Part 2: If no country code parsed yet, try fallback
    if (!parsedCountryCode && fallbackCountryCode) {
      const numericFallback = EarTag.countryCodes[fallbackCountryCode];
      if (numericFallback) {
        // `workingInput` at this point is the original input (cleaned, no recognized country code)
        // Assume `workingInput` is the national identifier part (must be 11 or 12 digits)
        if (workingInput.length === 12 || workingInput.length === 11) {
          parsedCountryCode = numericFallback;
          parsedNationalIdentifier = workingInput;
          // Update _raw to reflect the tag as if it had the country code
          this._raw = numericFallback + workingInput;
        }
      }
    }

    // Part 3: Finalize and set properties
    if (parsedCountryCode && parsedNationalIdentifier) {
      this._isoCountryCode = parsedCountryCode;
      if (parsedNationalIdentifier.length === 11) {
        // Pad 11-digit national ID to 12-digits by prepending "0"
        this._nationalIdentifier = "0" + parsedNationalIdentifier;
      } else if (parsedNationalIdentifier.length === 12) {
        this._nationalIdentifier = parsedNationalIdentifier;
      } else {
        // This case should ideally not be reached if lengths are checked properly before.
        this._isEartag = false;
        return;
      }
      this._isEartag = true;
    } else {
      this._isEartag = false;
    }
  }

  public static parse(
    str: string,
    fallbackCountryCode?: CountryCode | string | null
  ): EarTag {
    return new EarTag(str, fallbackCountryCode);
  }

  public static format(
    str: string,
    marketCountry?: string | null | undefined
  ): string | null {
    let t = EarTag.parse(str, marketCountry);
    if (t.isEartag()) {
      return t.toString();
    }
    return null;
  }

  toString(): string {
    if (!this._isEartag) {
      return "Invalid EarTag";
    }

    return `${
      EarTag.countryCodesByNumber[this.isoCountryCode!]
    } ${this.nationalIdentifier!.slice(0, 7)} ${this.nationalIdentifier!.slice(
      7
    )}`;
  }

  isISO24631(): boolean {
    return EarTag.regexISO24631.test(this._raw);
  }

  toISO24631(): string {
    if (!this.isISO24631()) {
      return "Not a ISO24631 EarTag";
    }
    // ISO 24631 Formatting for movements
    const res = EarTag.regexISO24631.exec(this._raw)!;
    return `${res[1]} ${res[2]} ${res[3]} ${res[4]} ${res[5]} ${res[6]} ${res[7]} ${res[8]}`;
  }
}
